import { useEffect, useMemo, useState } from "react";
import Layout from "../components/app/Layout";
import WineCard from "../components/app/WineCard";
import RequestQuoteModal from "../components/app/RequestQuoteModal";
import BuyRequestModal from "../components/app/BuyRequestModal";
import MockPaymentModal from "../components/app/MockPaymentModal";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function Wines() {
    const [filters, setFilters] = useState({ regions: [], grapes: [], vintages: [] });
    const [wines, setWines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState({ region: "", grape: "", vintage: "", min_price: 0, max_price: 1000, min_score: 0 });
    const [quoteWine, setQuoteWine] = useState(null);
    const [quoteMode, setQuoteMode] = useState("quote");
    const [buyWine, setBuyWine] = useState(null);
    const [paynowWine, setPaynowWine] = useState(null);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => { api.get("/wines/filters").then(({ data }) => setFilters(data)).catch(() => {}); }, []);

    const fetchWines = useMemo(() => async () => {
        setLoading(true);
        const params = {};
        if (active.region) params.region = active.region;
        if (active.grape) params.grape = active.grape;
        if (active.vintage) params.vintage = Number(active.vintage);
        if (active.min_price) params.min_price = active.min_price;
        if (active.max_price && active.max_price < 1000) params.max_price = active.max_price;
        if (active.min_score) params.min_score = active.min_score;
        try {
            const { data } = await api.get("/wines", { params });
            setWines(data);
        } finally { setLoading(false); }
    }, [active]);

    useEffect(() => { fetchWines(); }, [fetchWines]);

    const onQuote = (wine, mode = "quote") => {
        if (!user) {
            toast.info("Please log in to request a quote.");
            navigate(`/login?redirect=/wines`);
            return;
        }
        if (mode === "buy") {
            setBuyWine(wine);
            return;
        }
        if (mode === "paynow") {
            setPaynowWine(wine);
            return;
        }
        setQuoteMode(mode);
        setQuoteWine(wine);
    };

    const reset = () => setActive({ region: "", grape: "", vintage: "", min_price: 0, max_price: 1000, min_score: 0 });

    return (
        <Layout>
            <section className="px-6 md:px-12 lg:px-16 pt-20 md:pt-28 pb-16" data-testid="wines-header">
                <p className="overline">The Catalogue</p>
                <h1 className="font-display text-5xl md:text-7xl mt-4 leading-[0.95]">All Wines</h1>
                <p className="text-base md:text-lg text-[#A1A1AA] mt-6 max-w-xl leading-relaxed">
                    Curated bottles from verified Argentine wineries. FOB prices in USD.
                </p>
            </section>

            <section className="px-6 md:px-12 lg:px-16 pb-32 grid lg:grid-cols-[280px_1fr] gap-12">
                {/* Filters */}
                <aside className="self-start border-t border-[#27272A] pt-8" data-testid="wines-filters">
                    <div className="flex items-center justify-between mb-8">
                        <p className="overline text-[#C9A961]">Filter</p>
                        <button
                            onClick={reset}
                            className="text-[10px] tracking-[0.2em] uppercase text-[#71717A] hover:text-[#C9A961] transition-colors duration-500 border border-[#27272A] hover:border-[#C9A961] px-2.5 py-1"
                            data-testid="filter-reset"
                        >
                            Reset
                        </button>
                    </div>

                    <div className="space-y-10">
                        <FilterSelect label="Region" testid="filter-region" value={active.region} onChange={(v) => setActive({ ...active, region: v })} options={filters.regions} />
                        <FilterSelect label="Grape" testid="filter-grape" value={active.grape} onChange={(v) => setActive({ ...active, grape: v })} options={filters.grapes} />
                        <FilterSelect label="Vintage" testid="filter-vintage" value={active.vintage} onChange={(v) => setActive({ ...active, vintage: v })} options={filters.vintages} />

                        <div>
                            <div className="flex items-baseline justify-between mb-3">
                                <label className="overline">Min FOB USD</label>
                                <span className="text-xs text-[#C9A961] font-display">${active.min_price}</span>
                            </div>
                            <input type="range" min="0" max="500" step="10" value={active.min_price} onChange={(e) => setActive({ ...active, min_price: Number(e.target.value) })} className="w-full accent-[#C9A961]" data-testid="filter-min-price" />
                            <div className="flex justify-between mt-1.5 text-[10px] text-[#3F3F46]"><span>$0</span><span>$500</span></div>
                        </div>

                        <div>
                            <div className="flex items-baseline justify-between mb-3">
                                <label className="overline">Max FOB USD</label>
                                <span className="text-xs text-[#C9A961] font-display">${active.max_price}</span>
                            </div>
                            <input type="range" min="0" max="1000" step="10" value={active.max_price} onChange={(e) => setActive({ ...active, max_price: Number(e.target.value) })} className="w-full accent-[#C9A961]" data-testid="filter-max-price" />
                            <div className="flex justify-between mt-1.5 text-[10px] text-[#3F3F46]"><span>$0</span><span>$1000</span></div>
                        </div>

                        <div>
                            <div className="flex items-baseline justify-between mb-3">
                                <label className="overline">Min Score</label>
                                <span className="text-xs text-[#C9A961] font-display">{active.min_score}+</span>
                            </div>
                            <input type="range" min="0" max="100" step="1" value={active.min_score} onChange={(e) => setActive({ ...active, min_score: Number(e.target.value) })} className="w-full accent-[#C9A961]" data-testid="filter-min-score" />
                            <div className="flex justify-between mt-1.5 text-[10px] text-[#3F3F46]"><span>0</span><span>100 pts</span></div>
                        </div>
                    </div>
                </aside>

                {/* Grid */}
                <div data-testid="wines-grid">
                    <p className="font-display text-2xl text-[#E4E4E7] mb-10">
                        {loading ? "Loading..." : (
                            <>
                                {wines.length} <span className="text-[#71717A] text-lg tracking-wide">wine{wines.length !== 1 ? "s" : ""}</span>
                            </>
                        )}
                    </p>
                    {wines.length === 0 && !loading ? (
                        <div className="border border-[#27272A] p-20 text-center">
                            <p className="font-display text-3xl text-[#A1A1AA]">No wines match your filters.</p>
                            <button onClick={reset} className="mt-6 text-xs tracking-[0.2em] uppercase text-[#C9A961] hover:text-white transition-colors duration-500 border-b border-[#C9A961] hover:border-white pb-1">
                                Reset filters
                            </button>
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-8">
                            {wines.map((w) => <WineCard key={w.id} wine={w} onQuote={onQuote} />)}
                        </div>
                    )}
                </div>
            </section>

            {quoteWine && <RequestQuoteModal wine={quoteWine} mode={quoteMode} onClose={() => setQuoteWine(null)} />}
            {buyWine && <BuyRequestModal wine={buyWine} onClose={() => setBuyWine(null)} />}
            {paynowWine && (
                <MockPaymentModal
                    title={`Pagar — ${paynowWine.name}`}
                    subtitle={`${paynowWine.winery_name} · ${paynowWine.vintage}`}
                    amount={Math.round(paynowWine.price_fob_usd)}
                    currency="USD"
                    onClose={() => setPaynowWine(null)}
                    testId="wine-mock-pay"
                />
            )}
        </Layout>
    );
}

function FilterSelect({ label, value, onChange, options, testid }) {
    return (
        <div>
            <label className="overline block mb-3">{label}</label>
            <select className="input-line bg-black text-white" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
                <option value="">All</option>
                {options?.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>
    );
}
